import {
  Typography,
  useTheme,
  Box,
  useMediaQuery,
    styled,
    Paper,
    Stack,
  LinearProgress,
} from "@mui/material";
import { SchoolDashboardData } from "types/SchoolDashboardData";
import React from "react";
import SummaryDataBox from "./SummaryDataBox";
import BoxHeader from "./BoxHeader";
import FlexBetween from "./FlexBetween";
import { Pie, PieChart, Cell } from "recharts";
import { SchoolAggregateData } from "types/School";
import FlexBetweenBottom from "./FlexBetweenBottom";
import { Student, StudentAggregateData } from "types/Student";

const gridTemplateLargeScreens = `
  "a b c d"
  "e f g h"
`;

const gridTemplateSmallScreens = `
  "a"
  "b"
  "c"
  "d"
  "e"
  "f"
  "g"
  "h"
`;

const BottomAlignedContainer = styled(Box)({
  display: "flex",
  alignItems: "flex-end",
});

interface StudentReportProps {
  data: StudentAggregateData[] | [] | undefined;
}

const StudentReport = ({ data }: StudentReportProps) => {
  const { palette } = useTheme();
  const isAboveMediumScreen = useMediaQuery("(min-width: 1200px");
  const theme = useTheme();

  if (data === undefined || data.length === 0) {
    return (
      <Typography variant="h2">
        This student hasn't played the game yet.
      </Typography>
    );
  }

  // TODO: Figure out a way to represent multiple tests
  const firstTest = data[0];
  if (firstTest === undefined || firstTest === null) {
    return (
      <Typography variant="h2">
        This student hasn't played the game yet.
      </Typography>
    );
  }

  var overallPerformancePercentage = 0;
  var identificationPercentage = 0;
  var advancedIdentificationPercentage = 0;
  var memoryPercentage = 0;
  var numericalSkillsPercentage = 0;
  var reasoningPercentage = 0;
  var visualDiscriminationPercentage = 0;
  var auditoryDiscriminationPercentage = 0;

  if (
    firstTest &&
    (firstTest.overallPerformance || firstTest.overallPerformance === 0) &&
    firstTest.overallPerformanceTotal
  ) {
    overallPerformancePercentage = Math.round(
      (firstTest.overallPerformance / firstTest.overallPerformanceTotal) * 100
    );
  }
  if (
    firstTest &&
    (firstTest.identification || firstTest.identification === 0) &&
    firstTest.identificationTotal
  ) {
    identificationPercentage = Math.round(
      (firstTest.identification / firstTest.identificationTotal) * 100
    );
  }
  if (
    firstTest &&
    (firstTest.memory || firstTest.memory === 0) &&
    firstTest.memoryTotal
  ) {
    memoryPercentage = Math.round(
      (firstTest.memory / firstTest.memoryTotal) * 100
    );
  }
  if (
    firstTest &&
    (firstTest.numericalSkills || firstTest.numericalSkills === 0) &&
    firstTest.numericalSkillsTotal
  ) {
    numericalSkillsPercentage = Math.round(
      (firstTest.numericalSkills / firstTest.numericalSkillsTotal) * 100
    );
  }
  if (
    firstTest &&
    (firstTest.reasoning || firstTest.reasoning === 0) &&
    firstTest.reasoningTotal
  ) {
    reasoningPercentage = Math.round(
      (firstTest.reasoning / firstTest.reasoningTotal) * 100
    );
  }
  if (
    firstTest &&
    (firstTest.visualDiscrimination || firstTest.visualDiscrimination === 0) &&
    firstTest.visualDiscriminationTotal
  ) {
    visualDiscriminationPercentage = Math.round(
      (firstTest.visualDiscrimination / firstTest.visualDiscriminationTotal) *
        100
    );
  }
  if (
    firstTest &&
    (firstTest.auditoryDiscrimination ||
      firstTest.auditoryDiscrimination === 0) &&
    firstTest.auditoryDiscriminationTotal
  ) {
    auditoryDiscriminationPercentage = Math.round(
      (firstTest.auditoryDiscrimination /
        firstTest.auditoryDiscriminationTotal) *
        100
    );
  }

  const overallPerformanceData = [
    { name: "Correct", value: overallPerformancePercentage },
    { name: "Incorrect", value: 100 - overallPerformancePercentage },
  ];

  const identificationData = [
    { name: "Correct", value: identificationPercentage },
    { name: "Incorrect", value: 100 - identificationPercentage },
  ];

  const advancedIdentificationData = [
    { name: "Correct", value: advancedIdentificationPercentage },
    { name: "Incorrect", value: 100 - advancedIdentificationPercentage },
  ];

  const memoryData = [
    { name: "Correct", value: memoryPercentage },
    { name: "Incorrect", value: 100 - memoryPercentage },
  ];

  const numericalSkillsData = [
    { name: "Correct", value: numericalSkillsPercentage },
    { name: "Incorrect", value: 100 - numericalSkillsPercentage },
  ];

  const reasoningData = [
    { name: "Correct", value: reasoningPercentage },
    { name: "Incorrect", value: 100 - reasoningPercentage },
  ];

  const visualDiscriminationData = [
    { name: "Correct", value: visualDiscriminationPercentage },
    { name: "Incorrect", value: 100 - visualDiscriminationPercentage },
  ];

  const auditoryDiscriminationData = [
    { name: "Correct", value: auditoryDiscriminationPercentage },
    { name: "Incorrect", value: 100 - auditoryDiscriminationPercentage },
  ];

  //   const pieColors = [palette.primary[800], palette.primary[300]];
  const pieColors = [palette.primary[800], palette.primary[300]];
    const ProgressWithLabel: React.FC<{ label: string; value: number; color: string }> = ({ label, value, color }) => (
        <Box mb={2}>
            <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">{label}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {value}%
                </Typography>
            </Stack>
            <LinearProgress
                variant="determinate"
                value={value}
                sx={{
                    height: 8,
                    borderRadius: 5,
                    [`& .MuiLinearProgress-bar`]: {
                        backgroundColor: color,
                    },
                    backgroundColor: '#f0f0f0',
                }}
            />
        </Box>
    );
  return (
      <>
     <Box width="100%" marginTop="5px">
          {/* Cognitive Ability Averages */}
              <Paper elevation={1} sx={{ p: 2, maxWidth: 700, }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                  Cognitive Ability Averages
              </Typography>
              <ProgressWithLabel label="Identification" value={(identificationPercentage ? identificationPercentage : 0)} color="#f44336" />
              <ProgressWithLabel label="Memory" value={memoryPercentage ? memoryPercentage : 0} color="#673ab7" />
              <ProgressWithLabel label="Numerical Skills" value={(numericalSkillsData ? numericalSkillsPercentage : 0)} color="#2196f3" />
              <ProgressWithLabel label="Reasoning" value={(reasoningPercentage ? reasoningPercentage : 0)} color="#ffeb3b" />
              <ProgressWithLabel label="Visual Discrimination" value={(visualDiscriminationPercentage ? visualDiscriminationPercentage : 0)} color="#4caf50" />
              <ProgressWithLabel label="Auditory Discrimination" value={(auditoryDiscriminationPercentage ? auditoryDiscriminationPercentage : 0)} color="#800080" />
              </Paper>
          </Box>
      </>
  );
};

export default StudentReport;
